import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SeoSchema from "@/components/SeoSchema";
import MusicAutoSchema from "@/components/MusicAutoSchema";
import SinglesAutoSchema from "@/components/SinglesAutoSchema";
import Home from "@/pages/Home";
import Radio from "@/pages/Radio";
import Music from "@/pages/Music";
import Inspire from "@/pages/Inspire";
import BlogArticle from "@/pages/BlogArticle";
import Shop from "@/pages/Shop";
import EPK from "@/pages/EPK";
import About from "@/pages/About";
import Press from "@/pages/Press";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import Unsubscribe from "@/pages/Unsubscribe";
import SubscriptionConfirmed from "@/pages/SubscriptionConfirmed";
import FanClub from "@/pages/FanClub";
import Backstage from "@/pages/Backstage";
import LegalPage from "@/pages/LegalPage";
import DownloadLocker from "@/pages/DownloadLocker";
import DownloadSuccess from "@/pages/DownloadSuccess";
import Chronicles from "@/pages/Chronicles";
import ChronicleReader from "@/pages/ChronicleReader";
import CookieConsent from "@/components/CookieConsent";

function RouteScrollHandler() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Smooth-scroll to the hash anchor (e.g. /epk#contact, /inspire#weekly).
      // Defer one tick so the destination route has rendered its DOM first.
      const id = hash.slice(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo({ top: 0, behavior: "instant" });
      }, 100);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function ProtectedAdmin({ children }) {
  const { admin } = useAuth();
  if (admin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50 font-serif italic">
        Loading…
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && <SeoSchema />}
      {!isAdmin && <MusicAutoSchema />}
      {!isAdmin && <SinglesAutoSchema />}
      {!isAdmin && <Navbar />}
      <main className={isAdmin ? "" : "pt-16"}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/radio" element={<Radio />} />
          <Route path="/music" element={<Music />} />
          <Route path="/inspire" element={<Inspire />} />
          <Route path="/chronicles" element={<Chronicles />} />
          <Route path="/chronicles/:slug" element={<ChronicleReader />} />
          <Route path="/pro-vault" element={<Navigate to="/backstage#pro-vault" replace />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/subscription-confirmed" element={<SubscriptionConfirmed />} />
          <Route path="/fan-club" element={<FanClub />} />
          <Route path="/backstage" element={<Backstage />} />
          <Route path="/terms" element={<LegalPage />} />
          <Route path="/privacy" element={<LegalPage />} />
          <Route path="/refund" element={<LegalPage />} />
          <Route path="/download/success" element={<DownloadSuccess />} />
          <Route path="/download/:token" element={<DownloadLocker />} />
          <Route path="/epk" element={<EPK />} />
          <Route path="/about" element={<About />} />
          <Route path="/press" element={<Press />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedAdmin>
                <AdminDashboard />
              </ProtectedAdmin>
            }
          />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <CookieConsent />}
      <Toaster />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RouteScrollHandler />
          <Shell />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
